import React from 'react';
import { useTranslation } from 'react-i18next';
import { CatalogAttributeDefinition } from './catalogAttributes';
import styles from './Catalog.module.css';

type CatalogAttributeFieldsProps = {
  definitions: CatalogAttributeDefinition[];
  values: Record<string, string>;
  errors?: Record<string, string>;
  onChange: (attributeId: string, value: string) => void;
  ns?: 'products' | 'services';
};

export const formatAttributeValue = (definition: CatalogAttributeDefinition, value?: string, yesLabel = 'Yes', noLabel = 'No') => {
  const text = (value || '').trim();
  if (!text) return '-';
  if (definition.fieldType === 'yesNo') {
    return text === 'true' ? yesLabel : noLabel;
  }
  return text;
};

export const CatalogAttributeFields: React.FC<CatalogAttributeFieldsProps> = ({
  definitions,
  values,
  errors,
  onChange,
  ns = 'products',
}) => {
  const { t } = useTranslation([ns, 'common']);

  if (definitions.length === 0) {
    return null;
  }

  return (
    <div className={`${styles.field} ${styles.fieldFull}`}>
      <div className={styles.sectionTitle}>{t('attributesTitle', { ns })}</div>
      <p className={styles.fieldHint}>{t('attributesHint', { ns })}</p>
      <div className={styles.form}>
        {definitions.map(definition => {
          const value = values[definition.id] ?? '';
          const error = errors?.[definition.id] || errors?.[definition.key];
          return (
            <div key={definition.id} className={styles.field}>
              <label className={styles.label}>
                {definition.name}
                {definition.isRequired ? ' *' : ''}
              </label>
              {definition.fieldType === 'select' ? (
                <select className={styles.input} value={value} onChange={event => onChange(definition.id, event.target.value)}>
                  <option value="">{t('selectValue', { ns })}</option>
                  {definition.options.map((option: string) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              ) : definition.fieldType === 'yesNo' ? (
                <select className={styles.input} value={value} onChange={event => onChange(definition.id, event.target.value)}>
                  <option value="">{t('selectValue', { ns })}</option>
                  <option value="true">{t('yes', { ns })}</option>
                  <option value="false">{t('no', { ns })}</option>
                </select>
              ) : (
                <input
                  className={styles.input}
                  type={definition.fieldType === 'number' ? 'number' : definition.fieldType === 'date' ? 'date' : 'text'}
                  value={value}
                  onChange={event => onChange(definition.id, event.target.value)}
                />
              )}
              {definition.inherited && definition.sourceCategoryName ? (
                <div className={styles.fieldHint}>{t('inheritedFrom', { ns, name: definition.sourceCategoryName })}</div>
              ) : null}
              {error ? <div className={styles.fieldError}>{error}</div> : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};
